import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Document, Page } from 'react-pdf';
import '../../../super-picker-public/components/detail/_pdf/pdfWorker';
import FilePreviewDialog from '../../dialogs/FilePreviewDialog';
import {
  fetchPdfThumbnailBlob,
  fetchPreviewBlob,
} from '../../../project-essential/services/api';
import { triggerForceDownload as deliverForceDownload } from '../../../shared/utils/triggerForceDownload';
import ShareAssetButton from '../../../shared/components/ShareAssetButton';

// ---------------------------------------------------------------------------
// File Visuals
//
// Replicated from project-essential for the SuperPicker app.
// ---------------------------------------------------------------------------

type FileKind = {
  color: string;
  Icon: typeof PictureAsPdfIcon;
  label: string;
};

const FILE_KINDS: Record<string, FileKind> = {
  pdf:  { color: '#D32F2F', Icon: PictureAsPdfIcon, label: 'PDF' },
  doc:  { color: '#1565C0', Icon: ArticleIcon,      label: 'DOC' },
  docx: { color: '#1565C0', Icon: ArticleIcon,      label: 'DOC' },
  txt:  { color: '#546E7A', Icon: ArticleIcon,      label: 'TXT' },
  rtf:  { color: '#546E7A', Icon: ArticleIcon,      label: 'RTF' },
  xls:  { color: '#2E7D32', Icon: TableChartIcon,   label: 'XLS' },
  xlsx: { color: '#2E7D32', Icon: TableChartIcon,   label: 'XLS' },
  csv:  { color: '#2E7D32', Icon: TableChartIcon,   label: 'CSV' },
  ppt:  { color: '#E65100', Icon: SlideshowIcon,    label: 'PPT' },
  pptx: { color: '#E65100', Icon: SlideshowIcon,    label: 'PPT' },
  key:  { color: '#E65100', Icon: SlideshowIcon,    label: 'KEY' },
  png:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  jpg:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  jpeg: { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  gif:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'GIF' },
  webp: { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  svg:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'SVG' },
  mp4:  { color: '#3949AB', Icon: MovieIcon,        label: 'MP4' },
  webm: { color: '#3949AB', Icon: MovieIcon,        label: 'WEBM' },
  mov:  { color: '#3949AB', Icon: MovieIcon,        label: 'MOV' },
  m4v:  { color: '#3949AB', Icon: MovieIcon,        label: 'M4V' },
  zip:  { color: '#37474F', Icon: FolderZipIcon,    label: 'ZIP' },
  rar:  { color: '#37474F', Icon: FolderZipIcon,    label: 'RAR' },
  tar:  { color: '#37474F', Icon: FolderZipIcon,    label: 'TAR' },
  gz:   { color: '#37474F', Icon: FolderZipIcon,    label: 'GZ' },
};

const FALLBACK_FILE_KIND: FileKind = {
  color: '#757575',
  Icon: InsertDriveFileIcon,
  label: 'FILE',
};

const PREVIEWABLE_IMAGE_EXTS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
]);

const PREVIEWABLE_VIDEO_EXTS = new Set([
  'mp4',
  'webm',
  'mov',
  'm4v',
  'ogg',
  'ogv',
]);

function getExt(name: string): string {
  return (name.trim().split('.').pop() || '').toLowerCase();
}

export function isPreviewableImageFile(name: string): boolean {
  return PREVIEWABLE_IMAGE_EXTS.has(getExt(name));
}

export function isPdfFile(name: string): boolean {
  return getExt(name) === 'pdf';
}

/** See project-essential `formatLabelForWrap` — same filename wrap helper. */
function formatLabelForWrap(text: string): string {
  return text.replace(/([_-])/g, '$1\u200B');
}

export function isPreviewableFile(name: string): boolean {
  const ext = getExt(name);
  return (
    ext === 'pdf' ||
    PREVIEWABLE_IMAGE_EXTS.has(ext) ||
    PREVIEWABLE_VIDEO_EXTS.has(ext)
  );
}

export function isPreviewableVideo(name: string): boolean {
  return PREVIEWABLE_VIDEO_EXTS.has(getExt(name));
}

function getFileKind(name: string): FileKind {
  const ext = getExt(name);
  return FILE_KINDS[ext] || FALLBACK_FILE_KIND;
}

export function formatUploadedDate(rawDate: string | null): string {
  if (!rawDate) return '';
  try {
    const d = new Date(rawDate);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return '';
  }
}

export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined || bytes < 0) return '';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const exact = bytes / Math.pow(k, i);
  const decimals = i >= 2 ? 1 : 0;
  return `${exact.toFixed(decimals)} ${sizes[i]}`;
}

export function useTouchPreviewDevice(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
}

/**
 * Build a browser save-as filename from a human title (e.g. ebook name),
 * keeping a sensible extension from `fallbackFilename` or defaulting to `.pdf`.
 */
export function buildSafeDownloadFilename(
  preferredName: string,
  fallbackFilename?: string | null,
): string {
  const raw = (preferredName || fallbackFilename || 'download').trim();
  const base = raw.split(/[\\/]/).pop()?.trim() || 'download';
  const cleaned =
    base.replace(/[<>:"|?*\u0000-\u001f]/g, '').replace(/\s+/g, ' ').trim() || 'download';

  if (/\.[a-z0-9]{1,8}$/i.test(cleaned)) {
    return cleaned;
  }

  const fallbackExt = (fallbackFilename || '').match(/\.([a-z0-9]{1,8})$/i);
  if (fallbackExt) {
    return `${cleaned}.${fallbackExt[1]}`;
  }
  return `${cleaned}.pdf`;
}

export function triggerDownload(url: string, filename: string) {
  const safeName = buildSafeDownloadFilename(filename, filename);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  document.body.removeChild(link);
}

/** @see shared/utils/triggerForceDownload — WebView-safe blob delivery. */
export async function triggerForceDownload(url: string, filename: string): Promise<void> {
  const safeName = buildSafeDownloadFilename(filename, filename);
  await deliverForceDownload(url, safeName);
}

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);

function isPortalHostUrl(rawUrl: string): boolean {
  try {
    return new URL(rawUrl).hostname === 'portal.huttonsgroup.com';
  } catch {
    return false;
  }
}

/** Cap concurrent thumbnail downloads / renders to avoid iOS WebKit OOM. */
function createThumbGate(maxConcurrent: number) {
  let active = 0;
  const waiters: Array<() => void> = [];
  return {
    async acquire(): Promise<() => void> {
      if (active >= maxConcurrent) {
        await new Promise<void>((resolve) => {
          waiters.push(resolve);
        });
      }
      active += 1;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        active = Math.max(0, active - 1);
        const next = waiters.shift();
        if (next) next();
      };
    },
  };
}

/** pdf.js rasterises one document at a time (desktop only — see below). */
const pdfListThumbGate = createThumbGate(1);
/** Image thumbs are cheap to decode but still cap the parallel downloads. */
const imageThumbFetchGate = createThumbGate(4);

function snapshotPdfCanvasToObjectUrl(
  canvas: HTMLCanvasElement,
): Promise<string | null> {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          resolve(URL.createObjectURL(blob));
        },
        'image/jpeg',
        0.7,
      );
      return;
    }
    try {
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    } catch {
      resolve(null);
    }
  });
}

export function FilePreviewThumbnail({
  filename,
  url,
  previewImage,
  size = 'small',
}: {
  filename: string;
  url?: string | null;
  previewImage?: string | null;
  size?: 'small' | 'large';
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchPreview = useTouchPreviewDevice();
  const ext = getExt(filename);
  const isImage = IMAGE_EXTS.has(ext);
  const isPdf = ext === 'pdf';
  const isVideo = PREVIEWABLE_VIDEO_EXTS.has(ext);
  const hasUsableUrl = Boolean(url) && url !== '#';
  const hasPreviewImage = Boolean(previewImage);
  const isPortalUrl = hasUsableUrl && isPortalHostUrl(url!);
  // Images preview everywhere; videos stay desktop-only. PDFs are the awkward
  // one: rasterising a thumbnail in pdf.js means downloading the whole file
  // and parsing it, and a list of 30 MB brochures gets the iOS WebView killed
  // no matter how well the work is serialised. Desktop still renders locally;
  // touch devices ask the backend for a ~30 KB JPEG of page 1 instead.
  const needsProxyFetch =
    hasUsableUrl &&
    !hasPreviewImage &&
    isPortalUrl &&
    (isImage || (!touchPreview && (isPdf || isVideo)));
  const usesServerPdfThumbnail = touchPreview && isPdf && !hasPreviewImage && isPortalUrl;
  // Roughly 2× the CSS box so the JPEG still looks sharp on a retina screen.
  const pdfThumbnailRequestWidth = size === 'large' ? 256 : 160;

  const [previewFailed, setPreviewFailed] = useState(false);
  const [blobPreviewUrl, setBlobPreviewUrl] = useState<string | null>(null);
  const [pdfSnapshotUrl, setPdfSnapshotUrl] = useState<string | null>(null);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const thumbGateReleaseRef = useRef<(() => void) | null>(null);

  const releaseThumbGate = () => {
    thumbGateReleaseRef.current?.();
    thumbGateReleaseRef.current = null;
  };

  useEffect(() => {
    setPreviewFailed(false);
    setBlobPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPdfSnapshotUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    setInView(false);
    releaseThumbGate();
  }, [url, previewImage, filename]);

  useEffect(() => {
    if (hasPreviewImage || !hasUsableUrl || (!isPdf && !isImage && !isVideo)) return;
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setInView(entry.isIntersecting);
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasPreviewImage, hasUsableUrl, isPdf, isImage, isVideo]);

  useEffect(() => {
    if (!needsProxyFetch || !inView || previewFailed) return;
    if (isPdf && pdfSnapshotUrl) return;
    let cancelled = false;

    const run = async () => {
      const release = await (isPdf ? pdfListThumbGate : imageThumbFetchGate).acquire();
      if (cancelled) {
        release();
        return;
      }
      thumbGateReleaseRef.current = release;
      try {
        const { objectUrl } = await fetchPreviewBlob(url!);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          releaseThumbGate();
          return;
        }
        setBlobPreviewUrl(objectUrl);
        if (!isPdf) releaseThumbGate();
      } catch {
        if (!cancelled) setPreviewFailed(true);
        releaseThumbGate();
      }
    };

    void run();
    return () => {
      cancelled = true;
      releaseThumbGate();
    };
  }, [needsProxyFetch, inView, url, previewFailed, isPdf, pdfSnapshotUrl]);

  // Touch devices: the backend renders page 1 and we just show the JPEG.
  useEffect(() => {
    if (!usesServerPdfThumbnail || !inView || previewFailed || pdfSnapshotUrl) return;
    let cancelled = false;

    const run = async () => {
      const release = await imageThumbFetchGate.acquire();
      try {
        if (cancelled) return;
        const objectUrl = await fetchPdfThumbnailBlob(url!, pdfThumbnailRequestWidth);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPdfSnapshotUrl(objectUrl);
      } catch {
        // No thumbnail (unrenderable, oversized, renderer busy) — icon tile.
        if (!cancelled) setPreviewFailed(true);
      } finally {
        release();
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    usesServerPdfThumbnail,
    inView,
    url,
    previewFailed,
    pdfSnapshotUrl,
    pdfThumbnailRequestWidth,
  ]);

  useEffect(
    () => () => {
      if (blobPreviewUrl) URL.revokeObjectURL(blobPreviewUrl);
    },
    [blobPreviewUrl],
  );

  useEffect(
    () => () => {
      if (pdfSnapshotUrl?.startsWith('blob:')) URL.revokeObjectURL(pdfSnapshotUrl);
    },
    [pdfSnapshotUrl],
  );

  useEffect(() => {
    if (inView) return;
    setBlobPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    releaseThumbGate();
  }, [inView]);

  const showPreviewImage = hasPreviewImage && !previewFailed;
  const canPreviewImage =
    !showPreviewImage && hasUsableUrl && isImage && !previewFailed;
  const canPreviewPdf =
    !touchPreview && !showPreviewImage && hasUsableUrl && isPdf && !previewFailed;
  const canPreviewVideo =
    !touchPreview && !showPreviewImage && hasUsableUrl && isVideo && !previewFailed;
  const canPreview =
    canPreviewImage || canPreviewPdf || canPreviewVideo || Boolean(pdfSnapshotUrl);
  const effectivePreviewUrl =
    blobPreviewUrl ?? (needsProxyFetch ? null : url);

  const pdfRenderWidth = size === 'large' ? 360 : 200;

  const handlePdfPageRendered = () => {
    const canvas = containerRef.current?.querySelector('canvas');
    if (!(canvas instanceof HTMLCanvasElement)) {
      releaseThumbGate();
      return;
    }
    void snapshotPdfCanvasToObjectUrl(canvas).then((snapUrl) => {
      if (!snapUrl) {
        releaseThumbGate();
        return;
      }
      setPdfSnapshotUrl((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return snapUrl;
      });
      setBlobPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      releaseThumbGate();
    });
  };

  const { color, Icon } = getFileKind(filename);
  const fallbackIconSize = size === 'large' ? 22 : 18;
  const loadingIconSize = size === 'large' ? 26 : 22;

  return (
    <Box
      ref={containerRef}
      aria-hidden
      sx={{
        width:
          size === 'large' ? { xs: 82, sm: 96 } : { xs: 48, sm: 54 },
        height:
          size === 'large' ? { xs: 110, sm: 128 } : { xs: 64, sm: 72 },
        flexShrink: 0,
        borderRadius: size === 'large' ? 1.5 : 1,
        overflow: 'hidden',
        position: 'relative',
        border: showPreviewImage || canPreview
          ? '1px solid var(--color-border-strong)'
          : `1px solid ${color}33`,
        bgcolor: showPreviewImage || canPreview ? 'var(--color-surface-elevated)' : `${color}14`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& canvas': {
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto !important',
          height: 'auto !important',
          display: 'block',
        },
        '& .react-pdf__Page, & .react-pdf__Document': {
          background: 'transparent !important',
          maxWidth: '100%',
          maxHeight: '100%',
        },
      }}
    >
      {showPreviewImage ? (
        <Box
          component="img"
          src={previewImage!}
          alt=""
          loading="lazy"
          onError={() => setPreviewFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : pdfSnapshotUrl ? (
        <Box
          component="img"
          src={pdfSnapshotUrl}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : canPreviewImage && effectivePreviewUrl ? (
        <Box
          component="img"
          src={effectivePreviewUrl}
          alt=""
          loading="lazy"
          onError={() => setPreviewFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : canPreviewPdf && inView && effectivePreviewUrl ? (
        <Document
          file={effectivePreviewUrl}
          onLoadError={() => {
            setPreviewFailed(true);
            releaseThumbGate();
          }}
          onSourceError={() => {
            setPreviewFailed(true);
            releaseThumbGate();
          }}
          loading={<Icon sx={{ color, fontSize: loadingIconSize }} />}
          error={<Icon sx={{ color, fontSize: loadingIconSize }} />}
          noData={<Icon sx={{ color, fontSize: loadingIconSize }} />}
        >
          <Page
            pageNumber={1}
            width={pdfRenderWidth}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onRenderSuccess={handlePdfPageRendered}
            onRenderError={() => {
              setPreviewFailed(true);
              releaseThumbGate();
            }}
            loading={<Icon sx={{ color, fontSize: loadingIconSize }} />}
            error={<Icon sx={{ color, fontSize: loadingIconSize }} />}
            canvasBackground="#fff"
          />
        </Document>
      ) : canPreviewVideo && inView && effectivePreviewUrl ? (
        <>
          <Box
            component="video"
            src={effectivePreviewUrl}
            muted
            playsInline
            preload="metadata"
            onError={() => setPreviewFailed(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.28)',
              pointerEvents: 'none',
            }}
          >
            <PlayCircleOutlineIcon
              sx={{ color: '#fff', fontSize: size === 'large' ? 40 : 30 }}
            />
          </Box>
        </>
      ) : usesServerPdfThumbnail && inView && !previewFailed && !pdfSnapshotUrl ? (
        <Icon sx={{ color, fontSize: loadingIconSize }} />
      ) : canPreview && inView && needsProxyFetch && !effectivePreviewUrl && !previewFailed ? (
        <Icon sx={{ color, fontSize: loadingIconSize }} />
      ) : canPreview && !inView ? (
        <Icon sx={{ color, fontSize: loadingIconSize }} />
      ) : (
        <Icon sx={{ color, fontSize: fallbackIconSize }} />
      )}
    </Box>
  );
}

export interface DownloadFileRowProps {
  filename: string;
  displayName?: string;
  subtitle?: string;
  sizeBytes?: number;
  url?: string | null;
  urlPublic?: string | null;
  previewImage?: string | null;
  uploadedAt?: string | null;
  uploadedLabel?: string;
  onClick?: () => void;
  onPreview?: () => void;
  onDownloadClick?: () => void;
  /** Force disabling preview and always showing the trailing action instead. */
  disablePreview?: boolean;
  /** Trailing action: download file (default) or copy a link to the clipboard. */
  actionMode?: 'download' | 'copy';
  /** Text copied when `actionMode` is `'copy'`. Defaults to `url`. */
  copyText?: string | null;
  /** Replaces the auto-derived file-kind label on the meta line. */
  typeLabel?: string;
  /**
   * Fetch the file as a blob before downloading so cross-origin CDN
   * URLs save to disk instead of opening in a new tab.
   */
  forceBlobDownload?: boolean;
  /**
   * When set (admin-only surfaces), show a share control under the
   * download/copy action for EDM/eBook rows.
   */
  shareAsset?: {
    id: string;
    kind: 'edm' | 'ebook';
    title: string;
    projectId?: string | null;
  } | null;
}

export function DownloadFileRow({
  filename,
  displayName,
  subtitle,
  sizeBytes,
  url,
  urlPublic,
  previewImage,
  uploadedAt,
  uploadedLabel,
  onClick,
  onPreview,
  onDownloadClick,
  disablePreview,
  actionMode = 'download',
  copyText,
  typeLabel: typeLabelOverride,
  forceBlobDownload,
  shareAsset = null,
}: DownloadFileRowProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchPreview = useTouchPreviewDevice();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const disabled = !url;
  const label = displayName || filename;
  const downloadName = buildSafeDownloadFilename(label, filename);
  const textToCopy = (copyText ?? url ?? '').trim();
  const copyDisabled = !textToCopy;
  const hasUploadedAt = typeof uploadedAt === 'string' && uploadedAt.trim().length > 0;
  const dateText = hasUploadedAt ? (uploadedLabel ?? formatUploadedDate(uploadedAt)) : null;
  const sizeText = formatFileSize(sizeBytes ?? null);
  const { label: fileKindLabel } = getFileKind(filename);
  const typeLabel = typeLabelOverride ?? fileKindLabel;
  const metaPieces: string[] = [];
  if (typeLabel) metaPieces.push(typeLabel);
  if (sizeText) metaPieces.push(sizeText);
  const metaLine = metaPieces.join(' · ');

  const previewable = disablePreview ? false : isPreviewableFile(filename);
  const isImage = isPreviewableImageFile(filename);
  const isPdf = isPdfFile(filename);
  const rowOpensPreview =
    previewable && (!touchPreview || isImage || isPdf);
  const showDownloadButton =
    !previewable || (touchPreview && !isImage && !isPdf);
  const shouldForceBlobDownload = forceBlobDownload !== false;

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!url || downloading) return;
    if (onDownloadClick) onDownloadClick();
    setDownloading(true);
    try {
      if (shouldForceBlobDownload) {
        await triggerForceDownload(url, downloadName);
      } else {
        triggerDownload(url, downloadName);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!textToCopy) return;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — no-op.
    }
  };

  const handleRowClick = () => {
    if (onClick) {
      onClick();
    } else if (url && rowOpensPreview) {
      if (onPreview) onPreview();
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <Box
        onClick={handleRowClick}
        role={onClick || (url && rowOpensPreview) ? 'button' : undefined}
        tabIndex={onClick || (url && rowOpensPreview) ? 0 : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: { xs: 1.25, sm: 2 },
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          border: '1px solid var(--color-border-strong)',
          bgcolor: 'var(--color-surface)',
          cursor: onClick || (url && rowOpensPreview) ? 'pointer' : 'default',
          transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
          '&:hover': {
            borderColor: 'var(--color-accent)',
            boxShadow: 'inset 0 0 0 1px var(--color-accent)',
          },
        }}
      >
        <FilePreviewThumbnail
          filename={filename}
          url={url}
          previewImage={previewImage}
          size={isMobile ? 'small' : 'large'}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              lineHeight: 1.25,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              overflowWrap: 'anywhere',
              wordBreak: 'normal',
              whiteSpace: 'normal',
            }}
            title={label}
          >
            {formatLabelForWrap(label)}
          </Typography>
          {dateText && !isMobile && (
            <Typography
              variant="caption"
              sx={{
                color: 'var(--color-text-muted)',
                fontWeight: 500,
                mt: 0.25,
                fontSize: { xs: '0.72rem', sm: '0.75rem' },
                lineHeight: 1.3,
              }}
            >
              Uploaded {dateText}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: 'var(--color-text-muted)',
                mt: 0.25,
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                overflowWrap: 'anywhere',
                wordBreak: 'normal',
                whiteSpace: 'normal',
              }}
              title={subtitle}
            >
              {formatLabelForWrap(subtitle)}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {isMobile ? (
            (typeLabel || dateText || sizeText) && (
              <Stack spacing={0.25} sx={{ mt: 1 }}>
                {(typeLabel || sizeText) && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--color-text-muted)',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      fontSize: '0.78rem',
                    }}
                  >
                    {[typeLabel, sizeText].filter(Boolean).join(' · ')}
                  </Typography>
                )}
                {dateText && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--color-text-muted)',
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      lineHeight: 1.3,
                    }}
                  >
                    Uploaded {dateText}
                  </Typography>
                )}
              </Stack>
            )
          ) : (
            metaLine && (
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--color-text-muted)',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  fontSize: { xs: '0.78rem', sm: '0.82rem' },
                  mt: 1,
                }}
              >
                {metaLine}
              </Typography>
            )
          )}
        </Box>
        {(showDownloadButton || (shareAsset && url)) && (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={0.25}
            sx={{ flexShrink: 0, alignSelf: 'stretch', py: 0.5 }}
          >
            {showDownloadButton && (
              actionMode === 'copy' ? (
                <Tooltip title={copied ? 'Copied!' : 'Copy link'} arrow placement="left">
                  <span>
                    <IconButton
                      onClick={handleCopy}
                      disabled={copyDisabled}
                      aria-label="Copy link"
                      size="small"
                      sx={{
                        color: copied ? '#22c55e' : 'var(--color-accent)',
                        p: 0.5,
                        '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                        '&.Mui-disabled': { color: 'var(--color-border-strong)' },
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={downloading ? 'Preparing download…' : 'Download file'} arrow placement="left">
                  <span>
                    <IconButton
                      onClick={handleDownload}
                      disabled={disabled || downloading}
                      aria-label={downloading ? 'Preparing download' : 'Download file'}
                      size="small"
                      sx={{
                        color: 'var(--color-accent)',
                        p: 0.5,
                        '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                        '&.Mui-disabled': { color: 'var(--color-border-strong)' },
                      }}
                    >
                      {downloading ? (
                        <CircularProgress size={18} thickness={5} sx={{ color: 'var(--color-accent)' }} />
                      ) : (
                        <DownloadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )
            )}
            {shareAsset && url ? (
              <ShareAssetButton
                visible
                asset={{
                  id: shareAsset.id,
                  kind: shareAsset.kind,
                  title: shareAsset.title || label,
                  filename,
                  url,
                  projectId: shareAsset.projectId,
                }}
              />
            ) : null}
          </Stack>
        )}
      </Box>

      {url && rowOpensPreview && (
        <FilePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={label}
          url={url}
          urlPublic={urlPublic}
          filename={downloadName}
          onDownloadClick={onDownloadClick}
          forceBlobDownload={shouldForceBlobDownload}
        />
      )}
    </>
  );
}
